<template>
  <v-container>
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <Editor
      v-if="!isEditMode || (isEditMode && initialData)"
      ref="editor"
      :title="title"
      :initial-data="initialData"
      :loading="loading"
      type="pre-op"
      :is-edit-mode="isEditMode"
      @save="confirmSave"
      @cancel="confirmCancel"
      @delete="confirmDelete"
      @show-error="showError"
    />

    <!-- Delete Confirmation Dialog -->
    <ConfirmDialog
      ref="deleteDialog"
      v-model="dialogs.delete.show"
      :title="dialogs.delete.title"
      :content="dialogs.delete.content"
      titleClass="justify-center font-weight-bold text-h5 red-6--text"
      confirm-btn-text="確認刪除"
      confirm-btn-color="red-6"
      maxWidth="380"
      @confirm="deleteItem"
    />

    <!-- Unsaved Changes Dialog -->
    <ConfirmDialog
      v-model="dialogs.cancel.show"
      title="您所做的變更尚未儲存，確定要離開嗎？"
      content=""
      titleClass="justify-center font-weight-bold text-h5 tiffany-9--text"
      contentClass="mt-2 justify-center d-flex text-center"
      confirm-btn-text="繼續編輯"
      confirm-btn-color="tiffany-9"
      cancelBtnText="不儲存並離開"
      maxWidth="380"
      persistent
      @confirm="dialogs.cancel.show = false"
      @cancel="goBack(false)"
    />

    <!-- Success Dialogs (Create/Update) -->
    <ConfirmDialog
      v-model="dialogs.success.show"
      :title="dialogs.success.title"
      :content="dialogs.success.content"
      titleClass="justify-center font-weight-bold text-h5 tiffany-9--text"
      contentClass="mt-2 justify-center d-flex text-center"
      confirm-btn-text="點我前往"
      confirm-btn-color="tiffany-9"
      cancelBtnText="返回列表"
      maxWidth="380"
      persistent
      @confirm="goToAppointmentPage"
      @cancel="goBack(false)"
    />

    <v-snackbar v-model="snackbar.show" centered timeout="2000" color="red-6" dark>
      {{ snackbar.text }}
    </v-snackbar>

  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import Editor from '@/components/HealthEducation/Editor.vue';
import ConfirmDialog from '@/components/utils/ConfirmDialog.vue';
import {
  createHealthEducationAPI,
  updateHealthEducationAPI,
  deleteHealthEducationAPI,
  getSingleHealthEducationAPI
} from '@/api';

export default {
  name: 'HealthEducationForm',
  components: { Editor, ConfirmDialog },
  data() {
    return {
      loading: false,
      initialData: null,
      initialDataString: '',
      snackbar: {
        show: false,
        text: ''
      },
      dialogs: {
        delete: {
          show: false,
          title: '您確定要刪除嗎？',
          content: ''
        },
        cancel: {
          show: false,
        },
        success: {
          show: false,
          title: '',
          content: '歡迎前往約診頁面，\n開始發送術前衛教訊息。'
        }
      }
    };
  },
  computed: {
    ...mapGetters(['axiosParamData']),
    isEditMode() {
      return !!this.$route.params.id;
    },
    itemId() {
      return this.$route.params.id;
    },
    title() {
      return this.isEditMode ? '編輯客製化看診提醒' : '新增客製化看診提醒';
    },
    isDirty() {
      if (this.loading || !this.$refs.editor) return false;
      return JSON.stringify(this.$refs.editor.healthEducation) !== this.initialDataString;
    }
  },
  methods: {
    goBack(checkDirty = true) {
      if (checkDirty && this.isDirty) {
        this.dialogs.cancel.show = true;
      } else {
        this.$router.push({ name: 'HealthEducationList' });
      }
    },
    goToAppointmentPage() {
      this.$router.push({ name: 'home' });
    },
    confirmCancel() {
      this.goBack();
    },
    showError(message) {
      this.snackbar.text = message;
      this.snackbar.show = true;
    },
    setInitialData(data) {
      this.initialData = data;
      this.initialDataString = JSON.stringify(data);
    },
    async getHealthEducationItem() {
      this.loading = true;
      try {
        const res = await getSingleHealthEducationAPI(this.itemId, this.axiosParamData);
        const item = res.data;
        if (item) {
          const formattedData = {
            id: item.id,
            title: item.keyword,
            messages: [{
              id: `tmp_${Date.now()}`,
              type: item.json_content ? 'line_flex' : 'line_text_image',
              content: {
                text: item.text_content,
                json: item.json_content,
                images: item.img_url ? [item.img_url] : []
              }
            }]
          };
          this.setInitialData(JSON.parse(JSON.stringify(formattedData)));
        }
      } catch (error) {
        console.error('Failed to fetch health education item:', error);
        if (error.response && error.response.status === 404) {
          this.showError('項目不存在或已被刪除');
          setTimeout(() => this.goBack(false), 2000);
        } else {
          this.showError('讀取資料時發生錯誤');
        }
      } finally {
        this.loading = false;
      }
    },
    cleanData(data) {
      const formData = new FormData();
      const message = data.messages[0];

      formData.append('keyword', data.title);
      for (const [key, value] of Object.entries(this.axiosParamData)) {
        formData.append(key, value);
      }

      if (message.type === 'line_flex') {
        formData.append('json_content', message.content.json || '');
      } else {
        formData.append('text_content', message.content.text || '');
        if (message.content.images && message.content.images.length > 0) {
          const imageFile = message.content.images[0];
          if (imageFile instanceof File) {
            formData.append('img', imageFile);
          }
        }
      }
      return formData;
    },
    confirmSave(data) {
      this.save(data);
    },
    async save(data, leaveAfterSave = false) {
      this.loading = true;
      const payload = this.cleanData(data);
      try {
        if (this.isEditMode) {
          payload.append('_method', 'put');
          await updateHealthEducationAPI(this.itemId, payload);
          this.setInitialData(JSON.parse(JSON.stringify(data)));
          this.dialogs.success.title = '更新成功';
          this.dialogs.success.show = true;
        } else {
          await createHealthEducationAPI(payload);
          this.dialogs.success.title = '建立成功';
          this.dialogs.success.show = true;
        }
        if (leaveAfterSave) {
          // This will be handled by the success dialog's button
        }
      } catch (error) {
        console.error('Failed to save:', error);
        this.showError(error?.response?.data?.message || '操作失敗');
      } finally {
        this.loading = false;
      }
    },
    async saveAndLeave() {
      this.dialogs.cancel.show = false;
      await this.save(this.$refs.editor.healthEducation, true);
    },
    confirmDelete() {
      this.dialogs.delete.show = true;
    },
    async deleteItem() {
      this.loading = true;
      try {
        await deleteHealthEducationAPI(this.itemId, this.axiosParamData);
        this.showError('刪除成功');
        this.goBack(false);
      } catch (error) {
        console.error('Failed to delete:', error);
        this.showError(error?.response?.data?.message || '刪除失敗');
      } finally {
        this.loading = false;
        this.dialogs.delete.show = false;
      }
    }
  },
  created() {
    if (this.isEditMode) {
      this.getHealthEducationItem();
    }
  },
  mounted() {
    if (!this.isEditMode) {
      this.$nextTick(() => {
        this.setInitialData(JSON.parse(JSON.stringify(this.$refs.editor.healthEducation)));
      });
    }
  }
};
</script>